import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { listService, List, ListPOI } from '../services/listService';
import { ArrowLeft, Edit2, Save, X, Trash2, MapPin, GripVertical, Plus } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ListDetailPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState<List | null>(null);
  const [pois, setPois] = useState<ListPOI[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [removingPoiId, setRemovingPoiId] = useState<string | null>(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (uuid) {
      fetchListDetails();
    }
  }, [user, uuid, navigate]);

  const fetchListDetails = async () => {
    if (!uuid) return;

    try {
      setIsLoading(true);
      const data = await listService.getListByUuid(uuid);
      setList(data.list);
      setPois(data.pois);
      setEditedName(data.list.name);
      setEditedDescription(data.list.description || '');
      setHasOrderChanged(false);
      setError(null);
    } catch (err) {
      setError('Failed to load list details');
      console.error('Error fetching list details:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!uuid || !list) return;

    try {
      setIsSaving(true);
      const response = await listService.updateList(uuid, {
        name: editedName.trim(),
        description: editedDescription.trim() || undefined,
      });
      setList(response.list);
      setIsEditingDetails(false);
    } catch (err) {
      alert('Failed to update list details');
      console.error('Error updating list:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePOI = async (poiUuid: string, poiName: string) => {
    if (!uuid || !confirm(`Remove "${poiName}" from this list?`)) {
      return;
    }

    try {
      setRemovingPoiId(poiUuid);
      await listService.removePOIsFromList(uuid, { poi_uuids: [poiUuid] });
      setPois(pois.filter(poi => poi.uuid !== poiUuid));
      if (list) {
        setList({ ...list, poi_count: list.poi_count - 1 });
      }
    } catch (err) {
      alert('Failed to remove POI from list');
      console.error('Error removing POI:', err);
    } finally {
      setRemovingPoiId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setPois((items) => {
        const oldIndex = items.findIndex((item) => item.uuid === active.id);
        const newIndex = items.findIndex((item) => item.uuid === over.id);
        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        return reorderedItems.map((item, index) => ({ ...item, position: index }));
      });
      setHasOrderChanged(true);
    }
  };

  const handleSaveOrder = async () => {
    if (!uuid || !hasOrderChanged) return;
    try {
      setIsSavingOrder(true);
      await listService.reorderPOIs(uuid, { poi_uuids: pois.map(p => p.uuid) });
      setHasOrderChanged(false);
    } catch (err) {
      alert('Failed to save order');
      console.error('Error saving order:', err);
    } finally {
      setIsSavingOrder(false);
    }
  };

  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading list...</p>
        </div>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">{error || 'List not found'}</p>
          <Link
            to="/lists"
            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Lists
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Link
                to="/lists"
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Lists
              </Link>
              
              {isEditingDetails ? (
                <div className="mt-2 space-y-3">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    maxLength={255}
                    className="block w-full text-3xl font-bold border-b-2 border-blue-500 bg-transparent focus:outline-none text-gray-900 dark:text-white pb-1"
                    placeholder="List name"
                  />
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    maxLength={2000}
                    rows={2}
                    className="block w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Description (optional)"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveDetails}
                      disabled={isSaving}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingDetails(false);
                        setEditedName(list.name);
                        setEditedDescription(list.description || '');
                      }}
                      disabled={isSaving}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{list.name}</h1>
                    <button
                      onClick={() => setIsEditingDetails(true)}
                      className="ml-3 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                  </div>
                  {list.description && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{list.description}</p>
                  )}
                  <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>{list.poi_count} {list.poi_count === 1 ? 'place' : 'places'}</span>
                    <span>Updated {new Date(list.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to="/"
              state={{ openAddToListModal: true, listUuid: uuid }}
              className="ml-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Places
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {pois.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No places yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add places from the map to start building your list.
            </p>
            <div className="mt-6">
              <Link
                to="/"
                state={{ openAddToListModal: true, listUuid: uuid }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Places
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            {hasOrderChanged && (
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
                <span className="text-sm text-blue-700 dark:text-blue-300">Order changed — save to keep it.</span>
                <button
                  onClick={handleSaveOrder}
                  disabled={isSavingOrder}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {isSavingOrder ? 'Saving…' : 'Save order'}
                </button>
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={pois.map(poi => poi.uuid)} strategy={verticalListSortingStrategy}>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {pois.map((poi) => (
                    <SortablePOIItem
                      key={poi.uuid}
                      poi={poi}
                      onRemove={handleRemovePOI}
                      isRemoving={removingPoiId === poi.uuid}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </main>
    </div>
  );
};

// Sortable POI Item Component
interface SortablePOIItemProps {
  poi: ListPOI;
  onRemove: (uuid: string, name: string) => void;
  isRemoving: boolean;
}

const SortablePOIItem: React.FC<SortablePOIItemProps> = ({ poi, onRemove, isRemoving }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: poi.uuid,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
      <div className="px-4 py-4 sm:px-6">
        <div className="flex items-center">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing mr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <GripVertical className="h-5 w-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Link
                  to={`/app/${poi.uuid}`}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {poi.name}
                </Link>
                <div className="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <MapPin className="h-4 w-4 mr-1" />
                  {poi.city && poi.country ? `${poi.city}, ${poi.country}` : poi.city || poi.country || 'Location'}
                </div>
                {poi.description && (
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {poi.description}
                  </p>
                )}
              </div>
              
              <button
                onClick={() => onRemove(poi.uuid, poi.name)}
                disabled={isRemoving}
                className="ml-4 p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                title="Remove from list"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export default ListDetailPage;
